
import React from 'react';
import Layout from '@/components/Layout';
import { useLanguage } from '@/contexts/LanguageContext';
import { Building2, Camera, HeadphonesIcon } from 'lucide-react';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import SEOHead from '@/components/SEOHead';

const About = () => {
  const { t } = useLanguage();
  const { trackPropertyOwnerMoreInfo } = useAmplitudeEvents();

  const handleWhatsAppClick = () => {
    trackPropertyOwnerMoreInfo();
    const whatsappUrl = "https://api.whatsapp.com/send?phone=+523335781778&text=Hola!+Me+interesa+el+servicio+de+gesti%C3%B3n+de+propiedades.&utm_source=General";
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Layout>
      <SEOHead 
        title="Estancia Corta | Nosotros"
        description="¡Un buen alojamiento es el inicio de un gran viaje! Ofrecemos a nuestros huéspedes los mejores alojamientos en Manzanillo de super anfitriones en Airbnb."
        canonicalUrl="https://estanciacorta.com/nosotros"
      />
      {/* Hero Section */}
      <section className="py-10 md:py-20 bg-white min-h-[60vh] md:h-[70vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="lg:pr-8 text-center lg:text-left">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">{t('about.hero.title')}</h1>
              <div className="space-y-4 md:space-y-6">
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  {t('about.hero.description.first')}
                </p>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  {t('about.hero.description.second')}
                </p>
              </div>
              
              {/* Stats */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-6 sm:space-y-0 sm:space-x-8 lg:space-x-12 mt-8 md:mt-12">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">300+</div>
                  <div className="text-sm text-gray-600">{t('about.stats.bookings')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">1,000+</div>
                  <div className="text-sm text-gray-600">{t('about.stats.guests')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">4.97</div>
                  <div className="text-sm text-gray-600">{t('about.stats.rating')}</div>
                </div>
              </div>
            </div>
            <div className="lg:pl-8 order-first lg:order-last">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://res.cloudinary.com/dlqntmmhh/image/upload/v1749353197/manzanillo_playa_xyklnh.jpg" 
                  alt="Manzanillo beach" 
                  className="w-full h-64 md:h-80 lg:h-96 object-cover" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">{t('about.mission.title')}</h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              {t('about.mission.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('about.values.title')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🏔️</span>
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.values.excellence.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.values.excellence.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🤝</span>
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.values.hospitality.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.values.hospitality.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🌿</span>
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.values.sustainability.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.values.sustainability.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Property Owners Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('about.property_owners.title')}</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.property_owners.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.property_owners.management.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.property_owners.management.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Camera className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.property_owners.marketing.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.property_owners.marketing.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <HeadphonesIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">{t('about.property_owners.support.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.property_owners.support.description')}
              </p>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={handleWhatsAppClick}
              className="inline-block bg-blue-600 text-white px-6 md:px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold"
            >
              {t('about.property_owners.cta')}
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
